import React, { useCallback, useState } from "react";
import { Modal } from "./ui/ConfirmDialog";

/**
 * GdprModal – tlačítko „Informace o ochraně údajů" a dialog se zásadami
 * zpracování osobních údajů. Přihlašovací stránka ji vkládá jako
 * <GdprModal /> bez props; otevírání si řídí sama.
 *
 * Dialog jde do <body> přes portál: .lks-main má z-index 0 (vlastní kontext
 * vrstvení), takže modal vykreslený uvnitř obsahu stránky by zůstal pod
 * fixní hlavičkou.
 *
 * GdprDataPurposes – seznam shromažďovaných údajů a jejich účelu. Sdílí ho
 * stránka /gdpr (GDPRPage), aby dialog i stránka říkaly totéž.
 */

import { CONTACT_EMAIL } from "./utils/contact";

const DATA_PURPOSES: ReadonlyArray<{ label: string; purpose: string }> = [
  {
    label: "Jméno a příjmení",
    purpose: "Tvorba startovních listin, zobrazení výsledků",
  },
  {
    label: "E-mail",
    purpose:
      "Identifikátor uživatele pro přihlašování, ověření identity; upozornění na uvolněné místo na závodě, na jehož čekací listinu se zapíšete",
  },
  {
    label: "Telefonní číslo",
    purpose: "Kontakt v případě zrušení závodu, změn a chybných plateb",
  },
  {
    label: "Rok narození",
    purpose: "Zařazení do věkové kategorie, výpočet ceny startovného",
  },
  {
    label: "Pohlaví",
    purpose: "Zařazení do soutěžní kategorie, výpočet ceny startovného",
  },
  {
    // Registrace se ptá na státní občanství (pole nationality), ne na
    // národnost – ta by byla údajem o etnickém původu.
    label: "Státní občanství",
    purpose: "Zařazení do soutěžní kategorie (např. zahraniční účast)",
  },
  {
    label: "Číslo a platnost ZP",
    purpose: "Kontrola platnosti zbrojního průkazu podle legislativy",
  },
  {
    label: "Číslo dokladu totožnosti (OP, pas nebo EU zbrojní průkaz)",
    purpose:
      "Jen u závodů, kde to pořadatel vyžaduje, k ověření totožnosti; vidí ho jen vy a správci",
  },
  {
    label: "Informace o členství",
    purpose: "Výpočet ceny startovného (např. sleva pro členy)",
  },
  {
    label: "Registrace na závody",
    purpose: "Tvorba startovních listin, evidence výsledků",
  },
];

/** Seznam „údaj: účel" – dialog i stránka /gdpr. */
export const GdprDataPurposes: React.FC<{ className?: string }> = ({
  className,
}) => (
  <ul className={className ? `list-unstyled ${className}` : "list-unstyled"}>
    {DATA_PURPOSES.map((item) => (
      <li key={item.label} className="mb-2">
        <strong>{item.label}:</strong> {item.purpose}
      </li>
    ))}
  </ul>
);

/** Nadpis oddílu v dialogu (h2 je titulek dialogu). */
const HEADING_CLASS = "h6 fw-semibold mt-3 mb-2";

const GdprModal: React.FC = () => {
  const [show, setShow] = useState(false);
  const close = useCallback(() => setShow(false), []);

  const mailLink = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

  return (
    <>
      <button
        type="button"
        className="btn btn-outline-primary"
        aria-haspopup="dialog"
        onClick={() => setShow(true)}
      >
        <i className="bi bi-shield-lock me-2" aria-hidden="true" />
        Informace o ochraně údajů
      </button>

      {show
        ? (
            <Modal
              open
              onClose={close}
              title="Ochrana osobních údajů (GDPR)"
              size="lg"
              scrollBody
              footer={
                <div className="lks-modal__actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={close}
                  >
                    Zavřít
                  </button>
                </div>
              }
            >
              <p>
                Vaše údaje používáme pro registraci a správu účtu, přihlášky
                na závody, platby a zveřejňování výsledků v aplikaci LKS Apka.
                Nepředáváme je třetím stranám k jiným účelům.
              </p>
              <p>
                Aplikace slouží pro správu registrací a plateb účastníků
                sportovních závodů organizovaných LKS.
              </p>

              <h3 className={HEADING_CLASS}>
                Shromažďované údaje a jejich účel
              </h3>
              <GdprDataPurposes />

              <h3 className={HEADING_CLASS}>Kdo má k údajům přístup</h3>
              <p>
                Jméno a příjmení, číslo uživatele, státní občanství a výsledky
                jsou součástí veřejných startovních a výsledkových listin
                a živé tabule. Ostatní údaje zpracovávají správci klubu
                a pořadatelé závodů.
              </p>

              <h3 className={HEADING_CLASS}>Uchovávání a zabezpečení</h3>
              <p>
                Data jsou uložena v databázi Firebase (Google Cloud)
                a přenášena šifrovaně.
              </p>

              <h3 className={HEADING_CLASS}>Vaše práva</h3>
              <p>
                Máte právo na přístup, opravu, vymazání, omezení zpracování,
                přenositelnost údajů a odvolání souhlasu. Pro uplatnění
                kontaktujte: {mailLink}.
              </p>

              <h3 className={HEADING_CLASS}>Notifikace</h3>
              <p>
                Aplikace může zasílat notifikace – o nových závodech, změnách
                nebo platbách. Max. 5 měsíčně.
              </p>

              <h3 className={HEADING_CLASS}>Podpora</h3>
              <p className="mb-0">
                Dotazy lze zasílat na{" "}
                {mailLink}.
              </p>
            </Modal>
          )
        : null}
    </>
  );
};

export default GdprModal;
